import { Alert, Badge, Button, Group, Skeleton, Stack, Table, Text } from "@mantine/core";
import { IconBuildingBank, IconInfoCircle, IconPencil, IconPlus } from "@tabler/icons-react";
import { useState } from "react";
import useSWR from "swr";
import { AccountApi } from "../../../apis";
import type { TenantBankAccount } from "../../../apis/account";
import { AddBankAccountDrawer } from "../../../components/billing/AddBankAccountDrawer";
import { EditBankAccountDrawer } from "../../../components/billing/EditBankAccountDrawer";
import { EmptyState } from "../../../components/billing/EmptyState";
import { PageLayout } from "../../../components/layout";
import { activeLabel, PURPOSE_COLORS, purposeLabel, verifiedLabel } from "../../../utils/bank-account-labels";

const AccountsList = () => {
  const { data, isLoading, mutate } = useSWR("swr.my.bank-accounts", () => AccountApi.list());
  const rows: TenantBankAccount[] = data?.rows ?? [];

  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<TenantBankAccount | null>(null);

  return (
    <PageLayout
      title="Данс"
      subTitle="Төлбөр төлөх болон шимтгэлийн орлого хүлээн авах банкны данс."
      extra={[
        <Button key="add" leftSection={<IconPlus size={16} />} onClick={() => setAddOpen(true)}>
          Данс нэмэх
        </Button>
      ]}>
      <Stack gap="md">
        <Alert color="brand" variant="light" icon={<IconInfoCircle size={16} />}>
          <Text span fw={700}>«Орлого хүлээн авах»</Text> данс руу шимтгэлийн орлого орно;{" "}
          <Text span fw={700}>«Төлбөр төлөх»</Text> данснаас платформын төлбөр гарна.
        </Alert>

        {isLoading ? (
          <Stack gap="sm">
            <Skeleton height={40} radius="sm" />
            <Skeleton height={40} radius="sm" />
            <Skeleton height={40} radius="sm" />
          </Stack>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<IconBuildingBank size={26} />}
            message="Бүртгэлтэй данс алга байна. «Данс нэмэх» товчоор банкны дансаа баталгаажуулж нэмнэ үү."
          />
        ) : (
          <Table.ScrollContainer minWidth={900}>
            <Table verticalSpacing="sm" highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Банк</Table.Th>
                  <Table.Th>Дансны дугаар</Table.Th>
                  <Table.Th>Дансны нэр</Table.Th>
                  <Table.Th>Валют</Table.Th>
                  <Table.Th>Зориулалт</Table.Th>
                  <Table.Th>Үндсэн</Table.Th>
                  <Table.Th>Баталгаажсан</Table.Th>
                  <Table.Th>Төлөв</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((a) => (
                  <Table.Tr key={a.id}>
                    <Table.Td>{a.bankName ?? a.bankCode}</Table.Td>
                    <Table.Td style={{ fontVariantNumeric: "tabular-nums" }}>{a.accountNumber}</Table.Td>
                    <Table.Td>{a.accountName}</Table.Td>
                    <Table.Td>{a.currency}</Table.Td>
                    <Table.Td>
                      <Badge color={PURPOSE_COLORS[a.purpose] ?? "gray"} variant="light">
                        {purposeLabel(a.purpose)}
                      </Badge>
                    </Table.Td>
                    <Table.Td>{a.isDefault ? "Тийм" : "Үгүй"}</Table.Td>
                    <Table.Td>
                      <Badge color={a.isVerified ? "teal" : "yellow"} variant="light">
                        {verifiedLabel(a.isVerified)}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Badge color={a.isActive ? "green" : "gray"} variant="light">
                        {activeLabel(a.isActive)}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Group justify="flex-end" gap="xs" wrap="nowrap">
                        <Button
                          size="compact-xs"
                          variant="subtle"
                          leftSection={<IconPencil size={14} />}
                          onClick={() => setEditing(a)}>
                          Засах
                        </Button>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Stack>

      <AddBankAccountDrawer opened={addOpen} onClose={() => setAddOpen(false)} onCreated={() => mutate()} />
      <EditBankAccountDrawer
        account={editing}
        opened={!!editing}
        onClose={() => setEditing(null)}
        onSaved={() => mutate()}
      />
    </PageLayout>
  );
};

export default AccountsList;
